// Relative imports (not `@/`) so bun can test this module.
import { getHexTileMapKey } from '../hexmap/model/keys';
import type { HexTileMap } from '../hexmap/model/types';
import type { RuleSet } from '../rules/schema';
import {
  type Building,
  buildingLevel,
  buildingMaxHp,
  canUpgrade,
  cityBonuses,
  type EntityId,
  fromMilli,
  isCity,
  levelTitle,
  maxLevel,
  type PlayerId,
  type SimState,
  storageCapacity,
  territoryOwners,
  workerSlots,
} from '../sim';
import { assignedWorkers } from './activity';
import { labelOrDefault, type Names } from './names';
import { tickWorkers } from './tickCache';

/**
 * The player's buildings as the Buildings panel lists them (pure, no React):
 * grouped by the own city whose territory each stands in, a city's own row
 * heading its group, so it reads where everything is and what it's doing.
 */

/** A small glyph per building type for the list rows. */
const BUILDING_ICONS: Readonly<Record<string, string>> = {
  city: '🏰',
  farm: '🌾',
  forestry: '🌲',
  mine: '⛏️',
  barracks: '⚔️',
  factory: '🏭',
  'missile-silo': '🚀',
  'shield-generator': '🛡️',
  watchtower: '🗼',
};

export function buildingIcon(type: string): string {
  return BUILDING_ICONS[type] ?? '🏠';
}

export interface BuildingRow {
  building: Building;
  name: string;
  /** "Level 2/3", "Industrial 5/7", "Max level"; null for a building without levels or a site. */
  level: string | null;
  /** Workers against slots ("4/9 workers") or units stored ("3/5 stored"); null when neither. */
  load: { used: number; max: number; label: string } | null;
  /** Hp against max (whole hp), only while damaged. */
  hp: { hp: number; max: number } | null;
  /** The Upgrade button would work right now (`canUpgrade`). */
  upgradable: boolean;
  /** An unfinished site: how far built (0–1); null once complete. */
  progress: number | null;
}

export interface BuildingGroup {
  /** The city heading the group; null for buildings outside every own city's territory. */
  cityId: EntityId | null;
  /** "City 2", or "Outside your territory". */
  label: string;
  /** The city's own row (null for the outside group). */
  city: BuildingRow | null;
  rows: BuildingRow[];
}

/** "Level 2/3", "Industrial 5/7" for a titled level (a city's era), "Max level" at the top. */
export function levelText(rules: RuleSet, b: Building): string | null {
  const def = rules.buildings[b.type];
  if (!def?.levels || b.buildProgress < 1) return null;
  const level = buildingLevel(b);
  const max = maxLevel(def);
  const title = levelTitle(def, level);
  if (title !== def.name) return `${title} ${level}/${max}`;
  return level >= max ? 'Max level' : `Level ${level}/${max}`;
}

/**
 * Own buildings by city: groups in city-number order (sites of cities
 * included, numbered by `cityNumbers`), each with the city's row and the
 * buildings in its territory (by type, then id); anything outside every own
 * city's territory last.
 */
export function buildingGroups(
  state: SimState,
  rules: RuleSet,
  terrain: HexTileMap,
  playerId: PlayerId,
  cityNumbers: ReadonlyMap<EntityId, number>,
  /** The player's own city names (client-local). */
  names?: Names
): BuildingGroup[] {
  const ctx = { terrain, rules };
  const owners = territoryOwners(state, rules);
  const workers = tickWorkers(state, rules);
  const bonuses = cityBonuses(state, rules, owners);
  const row = (b: Building): BuildingRow => {
    const def = rules.buildings[b.type];
    const done = b.buildProgress >= 1;
    const max = buildingMaxHp(state, rules, b, bonuses);
    let load: BuildingRow['load'] = null;
    if (done && def?.provides) {
      load = {
        used: assignedWorkers(state, rules, b, workers).total,
        max: workerSlots(rules, b),
        label: 'workers',
      };
    } else if (done && storageCapacity(rules, b) > 0) {
      let used = 0;
      for (const u of Object.values(state.units)) if (u.inside === b.id) used++;
      load = { used, max: storageCapacity(rules, b), label: 'stored' };
    }
    return {
      building: b,
      name: def?.name ?? b.type,
      level: levelText(rules, b),
      load,
      hp: b.hp < max ? { hp: fromMilli(b.hp), max: fromMilli(max) } : null,
      upgradable: done && canUpgrade(state, ctx, playerId, b.id, owners).ok,
      progress: done ? null : b.buildProgress,
    };
  };

  const own = Object.values(state.buildings).filter((b) => b.owner === playerId);
  const cities = own
    .filter((b) => isCity(b, rules))
    .sort((a, b) => (cityNumbers.get(a.id) ?? 0) - (cityNumbers.get(b.id) ?? 0));
  const groups = new Map<EntityId | null, BuildingGroup>();
  for (const c of cities) {
    const n = cityNumbers.get(c.id);
    groups.set(c.id, {
      cityId: c.id,
      label: labelOrDefault(
        names,
        c.id,
        `${rules.buildings.city?.name ?? 'City'}${n === undefined ? '' : ` ${n}`}`
      ),
      city: row(c),
      rows: [],
    });
  }
  // Never by activity: rules order (farm, forestry, barracks, …), then oldest
  // first, so rows stay put under the cursor.
  const typeOrder = Object.keys(rules.buildings);
  const rank = (type: string) => {
    const i = typeOrder.indexOf(type);
    return i < 0 ? typeOrder.length : i;
  };
  const others = own
    .filter((b) => !isCity(b, rules))
    .sort(
      (a, b) =>
        rank(a.type) - rank(b.type) ||
        a.type.localeCompare(b.type) ||
        Number.parseInt(a.id.slice(1), 10) - Number.parseInt(b.id.slice(1), 10)
    );
  for (const b of others) {
    const owner = owners.get(getHexTileMapKey(b.pos));
    let group = groups.get(owner !== undefined && groups.has(owner) ? owner : null);
    if (!group) {
      group = { cityId: null, label: 'Outside your territory', city: null, rows: [] };
      groups.set(null, group);
    }
    group.rows.push(row(b));
  }
  // The outside group (added last to the map only if needed) stays last.
  return [...groups.values()].sort((a, b) => (a.cityId === null ? 1 : b.cityId === null ? -1 : 0));
}
